import config from 'constants/config'
import type { StripeElementLocale } from '@stripe/stripe-js'
import type {
  PaymentMethod,
  StripePaymentElementPaymentMethodType,
} from 'constants/payment'
import type { SupportedLocale } from 'i18n/supportedLocales'
import { isSupportedLocale } from 'i18n/util/localeMatcher'
import {
  STRIPE_CUSTOM_PAYTO_TYPE,
  STRIPE_PAYMENT_ELEMENT_GIROPAY_TYPE,
  STRIPE_PAYMENT_ELEMENT_IDEAL_TYPE,
  STRIPE_PAYMENT_ELEMENT_METHODS,
  STRIPE_PAYMENT_ELEMENT_CARD_TYPE,
  STRIPE_PAYMENT_ELEMENT_AFTERPAY_TYPE,
  STRIPE_PAYMENT_ELEMENT_KLARNA_TYPE,
  STRIPE_PAYMENT_ELEMENT_PAYNOW_TYPE,
  STRIPE_PAYMENT_ELEMENT_GRABPAY_TYPE,
  STRIPE_PAYMENT_ELEMENT_WECHATPAY_TYPE,
} from 'constants/payment'
import getPaymentMethods, { getPaymentMethodsByRegion } from 'lib/payment/getPaymentMethods'

export interface AccommodationMetaDataObject {
  deal_country: string
  lead_time?: number
  deal_destination: string
  is_refundable?: string
}

export const paymentElementMethodMapStripe: Utils.PartialRecord<PaymentMethod, StripePaymentElementPaymentMethodType> = {
  [STRIPE_PAYMENT_ELEMENT_CARD_TYPE]: 'card',
  [STRIPE_PAYMENT_ELEMENT_AFTERPAY_TYPE]: 'afterpay_clearpay',
  [STRIPE_PAYMENT_ELEMENT_GIROPAY_TYPE]: 'giropay',
  [STRIPE_PAYMENT_ELEMENT_IDEAL_TYPE]: 'ideal',
  [STRIPE_PAYMENT_ELEMENT_KLARNA_TYPE]: 'klarna',
  [STRIPE_PAYMENT_ELEMENT_PAYNOW_TYPE]: 'paynow',
  [STRIPE_PAYMENT_ELEMENT_GRABPAY_TYPE]: 'grabpay',
  [STRIPE_PAYMENT_ELEMENT_WECHATPAY_TYPE]: 'wechat_pay',
  [STRIPE_CUSTOM_PAYTO_TYPE]: 'payto',
}

export const getPaymentElementRedirectionURL = (orderId: string, transactionKey: string): string => {
  const regionCode = window.location.pathname.split('/')[1]
  const baseUrl = `${window.location.protocol}//${window.location.host}/${regionCode}`
  return `${baseUrl}/checkout/${transactionKey}/purchase?orderId=${orderId}&`
}

export const isStripePaymentElementCardEnabled = (regionCode: string, currencyCode: string): boolean => {
  return (
    !!regionCode &&
    !!currencyCode &&
    !!(config.STRIPE_PAYMENT_ELEMENT_ENABLED) &&
    (
      getPaymentMethodsByRegion(regionCode).includes(STRIPE_PAYMENT_ELEMENT_CARD_TYPE) ||
      getPaymentMethods(currencyCode).includes(STRIPE_PAYMENT_ELEMENT_CARD_TYPE)
    )
  )
}

export const isStripePaymentElementGiropayEnabled = (regionCode: string): boolean => !!regionCode && !!config.STRIPE_PAYMENT_ELEMENT_GIROPAY_ENABLED && getPaymentMethodsByRegion(regionCode).includes(STRIPE_PAYMENT_ELEMENT_GIROPAY_TYPE)

export const getStripePaymentElementMethods = (regionCode: string): Array<string> => regionCode ? (getPaymentMethodsByRegion(regionCode).filter(method => STRIPE_PAYMENT_ELEMENT_METHODS.includes(method))) : []

export const isStripeCustomPaytoEnabled = (currencyCode: string): boolean => !!currencyCode && !!config.STRIPE_PAYTO_ENABLED && getPaymentMethods(currencyCode).includes(STRIPE_CUSTOM_PAYTO_TYPE)

export const prepareInstalmentAdditionalInfoForStripe = (order: App.Order) => {
  try {
    return {
      // Null asserting here, since the bad dereference is being handled by try catch
      product_type: order.instalmentDetails?.items[0].offerType,
    }
  } catch {
    return {}
  }
}

export const isStripePaymentElementIDealEnabled = (regionCode: string): boolean => {
  const idealEnabled = getPaymentMethodsByRegion(regionCode).includes(STRIPE_PAYMENT_ELEMENT_IDEAL_TYPE)
  return !!regionCode && !!config.STRIPE_PAYMENT_ELEMENT_IDEAL_ENABLED && idealEnabled
}

export const isStripePaymentElementWeChatPayEnabled = (currencyCode: string): boolean => {
  return !!currencyCode && !!config.STRIPE_PAYMENT_ELEMENT_WECHATPAY_ENABLED && getPaymentMethods(currencyCode).includes(STRIPE_PAYMENT_ELEMENT_WECHATPAY_TYPE)
}

export const isStripePaymentElementKlarnaEnabled = (currencyCode: string): boolean => {
  return !!currencyCode && !!config.STRIPE_PAYMENT_ELEMENT_KLARNA_ENABLED && getPaymentMethods(currencyCode).includes(STRIPE_PAYMENT_ELEMENT_KLARNA_TYPE)
}

export const isStripePaymentElementPayNowEnabled = (currencyCode: string): boolean => {
  return !!currencyCode && !!config.STRIPE_PAYMENT_ELEMENT_PAYNOW_ENABLED && getPaymentMethods(currencyCode).includes(STRIPE_PAYMENT_ELEMENT_PAYNOW_TYPE)
}

export const isStripePaymentElementGrabPayEnabled = (currencyCode: string): boolean => {
  return !!currencyCode && !!config.STRIPE_PAYMENT_ELEMENT_GRABPAY_ENABLED && getPaymentMethods(currencyCode).includes(STRIPE_PAYMENT_ELEMENT_GRABPAY_TYPE)
}

// Stripe elements doesn't support full set of BCP-47 locales, and some languages like Hindi (hi) are not added yet
// so we can do this for now
const STRIPE_ELEMENTS_LOCALE_BY_SUPPORTED_LOCALE: Record<SupportedLocale, StripeElementLocale> = {
  'de-DE': 'de',
  'en-AU': 'en',
}

/**
 * Resolve the Stripe Elements locale from the current app locale.
 * Falls back to `auto` when the locale is unsupported or not enabled by remote config.
 * For now we normalize supported app locales to explicit Stripe locales.
 */
export function getStripeElementsLocale(
  locale: string | undefined,
  supportedLocales: Array<SupportedLocale>,
): StripeElementLocale {
  if (!locale || !isSupportedLocale(locale) || !supportedLocales.includes(locale)) {
    return 'auto'
  }

  return STRIPE_ELEMENTS_LOCALE_BY_SUPPORTED_LOCALE[locale] ?? 'auto'
}

/**
 * Shared Stripe Elements appearance configuration.
 * Used consistently across all Stripe Payment Element integrations.
 */
export const STRIPE_ELEMENTS_APPEARANCE = {
  variables: {
    colorPrimary: '#2d2926',
    fontSizeBase: '18px',
    fontSmooth: 'always',
    colorTextPlaceholder: '#999999',
    spacingUnit: '8px',
  },
} as const
